import React, { createContext, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { userAPI } from "../services/api";
import { useAuth } from "./AuthContext";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  let user = null;

  try {
    const auth = useAuth();
    user = auth?.user;
  } catch (error) {
    // AuthContext not available, which is fine
    console.debug("Auth context not available, running in anonymous mode");
  }

  const setDocumentAttributes = (lng) => {
    document.documentElement.lang = lng;
    document.documentElement.dir = ["ar"].includes(lng) ? "rtl" : "ltr";
  };

  // Initialize language and document attributes
  // Initialize language settings
  useEffect(() => {
    (async () => {
      try {
        // Try to get language from localStorage first
        const savedLang = localStorage.getItem("i18nextLng");
        let targetLang = savedLang;

        // If user is logged in, try to get their preference
        if (user) {
          try {
            const response = await userAPI.getSettings();
            if (response.data?.preferred_language) {
              targetLang = response.data.preferred_language;
            }
          } catch (error) {
            console.error("Failed to fetch user language preference:", error);
          }
        }

        // Change language if needed
        if (targetLang && targetLang !== i18n.language) {
          await i18n.changeLanguage(targetLang);
        }

        // Set document attributes
        setDocumentAttributes(i18n.language);
      } catch (error) {
        console.error("Failed to initialize language:", error);
        // Fallback to setting document attributes with current language
        setDocumentAttributes(i18n.language);
      }
    })();
    // Only depend on i18n since user is now constant within component lifecycle
  }, [i18n]);

  const changeLanguage = async (lang) => {
    try {
      if (!["en", "fr", "ar"].includes(lang)) {
        throw new Error(`Language ${lang} is not supported`);
      }

      // Update i18n language first
      await i18n.changeLanguage(lang);

      // Update document attributes
      setDocumentAttributes(lang);

      // Save to localStorage for persistence
      localStorage.setItem("i18nextLng", lang);

      // Only try to update backend if user is logged in
      if (user) {
        try {
          // Get current settings first
          const currentSettings = await userAPI.getSettings();
          // Update only the language while maintaining other settings
          await userAPI.updateSettings({
            ...currentSettings.data,
            preferred_language: lang,
          });
        } catch (error) {
          console.error(
            "Failed to save language preference to backend:",
            error
          );
          // Don't throw here as the language change was successful locally
        }
      }
    } catch (error) {
      console.error("Failed to change language:", error);
      throw error;
    }
  };

  return (
    <LanguageContext.Provider
      value={{ changeLanguage, currentLanguage: i18n.language }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
